import React, { useState, useEffect, useRef } from 'react';

const URL_REGEX = /(https?:\/\/[^\s<]+)/gi;

/**
 * Formats WhatsApp message text to HTML with:
 * 1. URL protection (extracted before formatting)
 * 2. HTML entity escaping
 * 3. WhatsApp formatting (*bold*, _italic_, ~strikethrough~, ```code```, `code`)
 * 4. URL restoration as clickable links
 * 5. Newline to <br/> conversion
 */
export const formatWhatsAppMessage = (text) => {
  if (!text) return '';

  // 1. Extract and protect URLs with placeholders
  const urls = [];
  let processed = text.replace(URL_REGEX, (match) => {
    urls.push(match);
    return `URLPLACEHOLDER${urls.length - 1}ENDURL`;
  });

  // 2. Escape HTML entities
  processed = processed
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  // 3. Apply WhatsApp formatting
  // Mentions: @Number and @Name
  processed = processed.replace(/@(\d{10,})/g, (match, id) => {
    return `<span class="font-semibold text-blue-600 dark:text-blue-300">@...${id.slice(-4)}</span>`;
  });
  processed = processed.replace(/@([A-Za-zÀ-ÿ][A-Za-zÀ-ÿ\s]{1,30})/g, '<span class="font-semibold text-blue-600 dark:text-blue-300">@$1</span>');

  // Triple backtick code blocks (must come before single backtick)
  processed = processed.replace(/```([\s\S]*?)```/g, '<code class="bg-gray-200 dark:bg-gray-700 px-1 rounded block whitespace-pre-wrap my-1">$1</code>');
  
  // Single backtick inline code
  processed = processed.replace(/`([^`]+?)`/g, '<code class="bg-gray-200 dark:bg-gray-700 px-1 rounded">$1</code>');

  // Bold: *text*
  processed = processed.replace(/\*([\s\S]*?)\*/g, '<strong>$1</strong>');

  // Italic: _text_
  processed = processed.replace(/_([\s\S]*?)_/g, '<em>$1</em>');

  // Strikethrough: ~text~
  processed = processed.replace(/~([\s\S]*?)~/g, '<del>$1</del>');

  // 4. Restore URLs as clickable <a> links with blue styling
  urls.forEach((url, i) => {
    const escapedUrl = url.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    const rawDisplayUrl = url.length > 72 ? `${url.slice(0, 69)}…` : url;
    const displayUrl = rawDisplayUrl.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    processed = processed.replace(
      `URLPLACEHOLDER${i}ENDURL`,
      `<a href="${escapedUrl}" target="_blank" rel="noopener noreferrer" class="underline break-all" style="color: #3b82f6;">${displayUrl}</a>`
    );
  });

  // 5. Convert newlines to <br/>
  processed = processed.replace(/\n/g, '<br/>');

  return processed;
};

/**
 * Strips HTML tags and returns plain text for preview purposes.
 * Useful for contact list message previews.
 */
export const formatWhatsAppPreview = (text, maxLength = 80) => {
  if (!text) return '';
  // Remove WhatsApp formatting markers for a clean preview
  let preview = text;
  preview = preview.replace(/```[\s\S]*?```/g, (m) => m.slice(3, -3));
  preview = preview.replace(/`([^`]+?)`/g, '$1');
  preview = preview.replace(/\*([\s\S]*?)\*/g, '$1');
  preview = preview.replace(/_([\s\S]*?)_/g, '$1');
  preview = preview.replace(/~([\s\S]*?)~/g, '$1');
  preview = preview.replace(/\n/g, ' ');
  if (maxLength && preview.length > maxLength) {
    preview = preview.substring(0, maxLength) + '…';
  }
  return preview;
};

const previewCache = {};
const LINK_PREVIEW_STYLE = { width: 280, maxWidth: '100%', height: 314 };

const LinkPreview = ({ url }) => {
  const [preview, setPreview] = useState(previewCache[url] || null);
  const [loading, setLoading] = useState(!previewCache[url]);
  const fetched = useRef(false);

  useEffect(() => {
    if (previewCache[url] !== undefined || fetched.current) {
      setLoading(false);
      return;
    }
    fetched.current = true;
    const apiBase = process.env.REACT_APP_API_URL || 'https://api.nomoz.tech';
    const token = localStorage.getItem('token');
    fetch(`${apiBase}/atendimento/link-preview?url=${encodeURIComponent(url)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(res => {
        const data = res.success ? res.data : null;
        previewCache[url] = data;
        setPreview(data);
      })
      .catch(() => { previewCache[url] = null; })
      .finally(() => setLoading(false));
  }, [url]);

  if (loading) {
    return (
      <div
        className="block mb-1 rounded-lg overflow-hidden bg-black/10 dark:bg-white/10 animate-pulse"
        style={LINK_PREVIEW_STYLE}
        aria-label="Carregando preview do link"
      >
        <div className="h-[220px] bg-black/10 dark:bg-white/10" />
        <div className="h-[94px] p-3 space-y-2">
          <div className="h-3 w-1/3 rounded bg-black/10 dark:bg-white/10" />
          <div className="h-4 w-4/5 rounded bg-black/10 dark:bg-white/10" />
          <div className="h-3 w-2/3 rounded bg-black/10 dark:bg-white/10" />
        </div>
      </div>
    );
  }

  if (!preview) {
    let hostname = url;
    try { hostname = new URL(url).hostname; } catch (_) {}
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
        className="flex mb-1 rounded-lg overflow-hidden border border-white/20 hover:opacity-90 transition-opacity items-center justify-center"
        style={{ ...LINK_PREVIEW_STYLE, textDecoration: 'none', color: 'inherit', background: 'rgba(0,0,0,0.12)' }}>
        <div className="text-center px-4">
          <div className="text-3xl mb-2">🔗</div>
          <p className="text-sm font-medium break-all line-clamp-2">{hostname}</p>
        </div>
      </a>
    );
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
      className="flex flex-col mb-1 rounded-lg overflow-hidden border border-white/20 hover:opacity-90 transition-opacity"
      style={{ ...LINK_PREVIEW_STYLE, textDecoration: 'none', color: 'inherit' }}>
      <div className="h-[220px] flex-shrink-0 bg-black/10 dark:bg-white/10">
        {preview.image ? (
          <img src={preview.image} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-4xl">🔗</div>
        )}
      </div>
      <div className="px-3 py-2 h-[94px] overflow-hidden" style={{ background: 'rgba(0,0,0,0.15)' }}>
        {preview.siteName && (
          <p className="text-[10px] uppercase opacity-60 mb-0.5">{preview.siteName}</p>
        )}
        {preview.title && (
          <p className="text-sm font-medium leading-tight line-clamp-2">{preview.title}</p>
        )}
        {preview.description && (
          <p className="text-xs opacity-70 mt-0.5 line-clamp-2">{preview.description}</p>
        )}
      </div>
    </a>
  );
};

const WhatsAppMessage = ({ text, className = '', hidePreview = false }) => {
  const urls = text?.match(URL_REGEX) || [];
  const firstUrl = urls[0];

  return (
    <div
      className={`${className} overflow-hidden`}
      style={firstUrl && !hidePreview ? { width: 280, maxWidth: '100%' } : undefined}
    >
      {firstUrl && !hidePreview && <LinkPreview url={firstUrl} />}
      <div className="break-words overflow-hidden" dangerouslySetInnerHTML={{ __html: formatWhatsAppMessage(text) }} />
    </div>
  );
};

export default WhatsAppMessage;
